import { Alert, Button, Card, DatePicker, Form, Input, InputNumber, Select, TreeSelect } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import campaignApi from 'src/component/app/campaign/CampaignApi'
import * as routes from 'src/constants/routes'
import { useUser } from 'src/context/UserContext'
import { AdvertiserBean, CampaignBean, LanguageBean, SspBean } from '../../../api/api'
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader'
import { capitalizedView, getIabCategoriesAsTree, isAdmin } from '../../../util/Util'
import AnchorWrapper from '../anchor/AnchorWrapper'
import geoApi from '../geo/GeoApi'
import GeoTargeting, { GeoItem, toBean } from '../geo/GeoTargeting'
import { fromBean, MapPoint, MapSelector } from '../map/MapSelector'
import { WithLoadedMap } from '../map/WithLoadedMap'
import { anchorItems, getGeoPrecision } from './CampaignCreate'
import SspTrafficSelector from './SspTrafficSelector'

const FormItem = Form.Item
const Option = Select.Option

const formInfoItems: FormDataType[] = [
    FormDataType.Ssps,
    FormDataType.Languages,
    FormDataType.Advertisers,
    FormDataType.IabCategories
]

const CampaignEdit = () => {

    const [info, setInfo] = useState({
        languages: [],
        advertisers: [],
        ssps: [],
        iabCategories: []
    })

    const navigateTo = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [error, setError] = useState(undefined)
    const [form] = Form.useForm()
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    const [geoItems, setGeoItems] = useState<GeoItem[]>([])

    const [mapPoint, setMapPoint] = useState<MapPoint>({})
    const [mapVisible, setMapVisible] = useState(false)

    const handleSubmit = async () => {
        const values = await form.validateFields()
        try {
            await campaignApi.edit(id, { ...values, startDate, endDate, geoItems: geoItems.map(toBean), ...mapPoint })
            navigateTo('/campaign')
        } catch (e) {
            setError(e.error)
        }
    }

    const loadData = async () => {
        try {
            const response: any = await campaignApi.view(id)
            const bean = response.data

            if (bean.startDate) {
                setStartDate(bean.startDate)
                bean.startDate = dayjs(bean.startDate, 'YYYY-MM-DD HH:mm')
            }
            if (bean.endDate) {
                setEndDate(bean.endDate)
                bean.endDate = dayjs(bean.endDate, 'YYYY-MM-DD HH:mm')
            }
            form.setFieldsValue(bean)

            setGeoItems(bean.geoItems)

            setMapPoint(fromBean(bean))

            const formInfo = await formDataLoader(...formInfoItems)
            setInfo(formInfo as any)
        } catch (e) {
            setError(e.error)
        }
    }

    useEffect(() => {
        loadData()
    }, [id])

    const totalBudget = Form.useWatch('totalBudget', form)
    const sspBudgets = Form.useWatch('sspBudgets', form)

    return <div className="anchored-form">
        <Form
            style={{
                width: '1000px'
            }}
            onFinish={handleSubmit}
            form={form}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 16 }}
        >
            {error ? <Alert message={error} type="error" /> : null}
            <Card title="General" size="small" className="form-card" id="general">
                <FormItem
                    required
                    name="name"
                    label="Name"
                >
                    <Input placeholder="Name" />
                </FormItem>
                <FormItem
                    name="status"
                    label="Status"
                    required
                >
                    <Select placeholder="Select Status">
                        {Object.values(CampaignBean.StatusEnum).map(type => <Option key={type} value={type}>{capitalizedView(type)}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="advertiserId"
                    required
                    label="Advertiser"
                >
                    <Select
                        showSearch
                        optionFilterProp="title"
                        placeholder="Select Advertiser"
                    >
                        {info.advertisers.map((item: AdvertiserBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="sspIds"
                    label="SSP list"
                >
                    <SspTrafficSelector
                        ssps={info.ssps as SspBean[]}
                        budgets={sspBudgets}
                        totalBudget={totalBudget}
                        onBudgetsChange={(value) => form.setFieldValue('sspBudgets', value)}
                    />
                </FormItem>
                <FormItem
                    hidden
                    name="sspBudgets"
                >
                    <Input />
                </FormItem>
                <FormItem
                    label="Dates"
                    required
                >
                    <FormItem
                        name="startDate"
                        className="ant-item ant-item-first form-date-item"

                    >
                        <DatePicker showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }} format="YYYY-MM-DD HH:mm" onChange={(moment, value) => setStartDate(value as string)} placeholder="Start date" />
                    </FormItem>
                    <FormItem
                        name="endDate"
                        className="ant-item ant-item-second form-date-item"

                    >
                        <DatePicker showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }} format="YYYY-MM-DD HH:mm" onChange={(moment, value) => setEndDate(value as string)} placeholder="End date" />
                    </FormItem>
                </FormItem>
                <FormItem
                    required
                    name="totalBudget"
                    label="Total Budget"
                    className="form-date-item"
                >
                    <InputNumber placeholder="Total Budget" suffix="$" />
                </FormItem>
                <FormItem
                    name="dailyBudget"
                    label="Daily Budget"
                    className="form-date-item"
                >
                    <InputNumber placeholder="Daily Budget" suffix="$" />
                </FormItem>
            </Card>
            <Card title="Targeting" size="small" className="form-card" id="targeting">
                <FormItem
                    name="languages"
                    label="Languages"
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select language"
                    >
                        {info.languages.map((item: LanguageBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="devices"
                    label="Devices"
                >
                    <Select mode="multiple" placeholder="Select device">
                        {Object.keys(CampaignBean.DevicesEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="brands"
                    label="Brands"
                >
                    <Select mode="multiple" placeholder="Select brand">
                        {Object.keys(CampaignBean.BrandsEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="platforms"
                    label="Platforms"
                >
                    <Select mode="multiple" placeholder="Select platform">
                        {Object.keys(CampaignBean.PlatformsEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="browsers"
                    label="Browsers"
                >
                    <Select mode="multiple" placeholder="Select browser">
                        {Object.keys(CampaignBean.BrowsersEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="contentIabCategoryIds"
                    label="Content IAB Categories"
                >
                    <TreeSelect
                        allowClear
                        treeCheckable
                        showCheckedStrategy="SHOW_ALL"
                        placeholder="Select content IAB categories"
                        treeData={getIabCategoriesAsTree(info.iabCategories)}
                    />
                </FormItem>
            </Card>
            <Card title="Geo" size="small" className="form-card" id="geo">
                <FormItem label="Coordinates" className="compact-ant-item">
                    Latitude: {mapPoint.lat ? mapPoint.lat : <span className="form-item-text">not set</span>}
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }} className="compact-ant-item">
                    Longitude: {mapPoint.lon ? mapPoint.lon : <span className="form-item-text">not set</span>}
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }}>
                    Radius: {mapPoint.radius ? mapPoint.radius : <span className="form-item-text">not set</span>}
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }}>
                    <Button className="geo-btn" htmlType="button" type="primary" ghost style={{ width: '14.5rem', borderRadius: '8px' }} onClick={() => setMapVisible(true)} icon={<img src="/resources/images/map_point.svg" alt="" />}>
                        Set Geotargeting
                    </Button>
                </FormItem>
                <WithLoadedMap>
                    <MapSelector
                        point={mapPoint}
                        setPoint={setMapPoint}
                        mapVisible={mapVisible}
                        setMapVisible={setMapVisible}
                    />
                </WithLoadedMap>
                <FormItem
                    name="geoPrecision"
                    label="Geo Precision"
                >
                    <Select placeholder="Select Geo Precision">
                        {Object.values(CampaignBean.GeoPrecisionEnum).map((type: CampaignBean.GeoPrecisionEnum) => <Option key={type} value={type}>{getGeoPrecision(type)}</Option>)}
                    </Select>
                </FormItem>
                <GeoTargeting
                    id={id}
                    loadGeo={campaign => geoApi.campaignGeo(campaign)}
                    geoItems={geoItems}
                    setGeoItems={setGeoItems}
                />
            </Card>
            <div className="create-cancel-group">
                <Button type="primary" htmlType="submit">Save</Button>
                <Button htmlType="button" danger onClick={() => navigateTo(routes.CAMPAIGN)}>Cancel</Button>
            </div>
        </Form>
        <AnchorWrapper items={anchorItems} />
    </div>
}

export default CampaignEdit


// WEBPACK FOOTER //
// ./src/component/app/campaign/CampaignEdit.tsx